import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSwipeable } from 'react-swipeable';
import MarkdownIt from 'markdown-it';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import './App.css';
import { Routes, Route, useNavigate } from 'react-router-dom';
import easterEggUrl from './easteregg.txt';
import { DDWN_API_BASE } from './apiConfig';

// Router Pages
import MegaGame from './MegaGame';
import CypherApp from './cypher/CypherApp';

// Custom Markdown-it plugin for wikilinks
function wikiLinkPlugin(md) {
  function tokenizeWikiLink(state, silent) {
    const marker = '[[';
    const markerClose = ']]';
    const pos = state.pos;
    const max = state.posMax;

    // Check if the starting pattern is present
    if (state.src.slice(pos, pos + marker.length) !== marker) return false;

    // Look for the closing marker
    const end = state.src.indexOf(markerClose, pos + marker.length);
    if (end === -1) return false;

    // Extract the link text between the markers
    const content = state.src.slice(pos + marker.length, end).trim();
    if (!content) return false;

    // In silent mode, don't output any tokens
    if (silent) {
      state.pos = end + markerClose.length;
      return true;
    }

    // Create an opening link token with href pointing to ?item=content
    let token = state.push('link_open', 'a', 1);
    token.attrs = [['href', `?item=${encodeURIComponent(content)}`]];

    // Create a text token for the link text
    token = state.push('text', '', 0);
    token.content = content;

    // Create a closing link token
    state.push('link_close', 'a', -1);

    // Move the current position past the closing marker
    state.pos = end + markerClose.length;
    return true;
  }

  // Insert the rule before the built-in link rule
  md.inline.ruler.before('link', 'wikilink', tokenizeWikiLink);
}

const mdParser = new MarkdownIt();
mdParser.use(wikiLinkPlugin);

const categories = [
  { value: 'races', label: 'Race' },
  { value: 'class', label: 'Class' },
  { value: 'backgrounds', label: 'Background' },
  { value: 'items', label: 'Item' },
  { value: 'spells', label: 'Spell' },
  { value: 'expanded-spells', label: 'Spells (Expanded)' },
  { value: 'monsters', label: 'Monster' },
  { value: 'feats', label: 'Feat' },
  { value: 'classarts', label: 'Class Arts' },
	  {value: 'classprefabs', label: 'Class Prefabs' },
    { value: 'rules', label: 'Game Mechanics' },
];

function HomeApp() {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [masterSearchEnabled, setMasterSearchEnabled] = useState(false);
  const [showDDSpells, setShowDDSpells] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newEntry, setNewEntry] = useState({
    name: '',
    description: '',
    category: categories[0].value
  });
  // Easter egg state: store the text and optional override for display
  const [easterEggText, setEasterEggText] = useState(null);
  const [easterOverride, setEasterOverride] = useState(null);
  // Track which categories are collapsed in the sidebar list
  const [collapsedCategories, setCollapsedCategories] = useState({});
  // Dark mode state, persisted in a cookie
  const [darkMode, setDarkMode] = useState(() => {
    const cookieValue = document.cookie.replace(
      /(?:(?:^|.*;\s*)darkMode\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    );
    return cookieValue === 'true';
  });
  
  // Initialize Navigator
  const navigate = useNavigate();
  
  // Initialize sidebarCollapsed based on a cookie value.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const cookieValue = document.cookie.replace(
      /(?:(?:^|.*;\s*)sidebarCollapsed\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    );
    return cookieValue === 'true';
  });
    // Save the sidebar state to a cookie whenever it changes.
  useEffect(() => {
    document.cookie = `sidebarCollapsed=${sidebarCollapsed}; path=/;`;
  }, [sidebarCollapsed]);

  // Apply and persist dark mode
  useEffect(() => {
    document.cookie = `darkMode=${darkMode}; path=/;`;
    document.body.classList.toggle('dark-mode', darkMode);
	  document.documentElement.setAttribute('data-color-mode', darkMode ? 'dark' : 'light');
    return () => document.body.classList.remove('dark-mode');
  }, [darkMode]);

	//use &search parameter to prepopulate search box
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const searchQuery = params.get('search');
    if (searchQuery) {
      setSearchTerm(decodeURIComponent(searchQuery));
    }
  }, []);


  // Prefetch easter egg text once (non-blocking)
  useEffect(() => {
    fetch(easterEggUrl)
      .then(r => r.text())
      .then(setEasterEggText)
      .catch(() => {});
  }, []);

  // Fetch data from multiple endpoints and add the category property.
  useEffect(() => {
    const endpoints = categories.map(cat => ({
      category: cat.value,
      url: `${DDWN_API_BASE}/${cat.value}`
    }));

    Promise.all(
      endpoints.map(({ category, url }) =>
        fetch(url)
          .then(response => response.json())
          .then(result => {
            // Handle the response format where data is under category key
            const items = result[category] || [];
            return items.map(item => ({ ...item, category }));
          })
          .catch(error => {
            console.error(`Error fetching ${category} data:`, error);
            return [];
          })
      )
    )
      .then(results => {
        const combinedData = results.flat();
        setData(combinedData);
      })
      .catch(error => console.error('Error fetching data:', error));
  }, []);
  
  //Auto query item if specified in URL  
    useEffect(() => {
    if (data.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    // Try to get a parameter named "item". If not, use the whole query string.
    let query = params.get('item');
    if (!query && window.location.search.length > 1) {
      query = decodeURIComponent(window.location.search.substring(1));
    }
    if (query) {
      // Find an entry with a name that matches the query (case-insensitive)
      const match = data.find(item => item.name.toLowerCase() === query.toLowerCase());
      if (match) {
        setSelectedItem(match);
      }
    }
  }, [data]);

  // Toggle sidebar function and swiping
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
 
    const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      // For example, if the sidebar is open, swiping left closes it
      if (!sidebarCollapsed) toggleSidebar();
    },
    onSwipedRight: () => {
      // And if the sidebar is closed, swiping right opens it
      if (sidebarCollapsed) toggleSidebar();
    },
    // Optionally adjust the swipe threshold
    delta: 50,
  });

  // Enhanced filter function that checks both name and category
const filteredResults = data.filter(item => {
  // Hide 'expanded-spells' category items if showDDSpells is false
  if (!showDDSpells && item.category === 'expanded-spells') return false;

  const searchTerms = searchTerm.toLowerCase().split(/\sor\s+/);

  return searchTerms.some(term => {
    const nameMatch = item.name.toLowerCase().includes(term);
    const categoryValue = item.category.toLowerCase();
    const categoryLabel = categories.find(c => c.value === item.category)?.label.toLowerCase() || '';
    const categoryMatch = categoryValue.includes(term) || categoryLabel.includes(term);

    const descriptionMatch = masterSearchEnabled
      ? item.description.toLowerCase().includes(term)
      : false;

    return nameMatch || categoryMatch || descriptionMatch;
  });
});
  // Sort results to group by category
  const sortedResults = [...filteredResults].sort((a, b) => {
    // First sort by whether it's a category match
    const searchLower = searchTerm.toLowerCase();
    const aCatMatch = a.category.toLowerCase().includes(searchLower) || 
                     categories.find(c => c.value === a.category)?.label.toLowerCase().includes(searchLower);
    const bCatMatch = b.category.toLowerCase().includes(searchLower) || 
                     categories.find(c => c.value === b.category)?.label.toLowerCase().includes(searchLower);
    
    if (aCatMatch && !bCatMatch) return -1;
    if (!aCatMatch && bCatMatch) return 1;
    
    // Then sort by category and then by name
    if (a.category !== b.category) {
      return a.category.localeCompare(b.category);
    }
    return a.name.localeCompare(b.name);
  });

  // Handlers for editing existing entry.
  const handleEditClick = () => {
    setIsEditing(true);
    setEditedDescription(selectedItem.description);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedDescription('');
  };

  const handleEditorChange = (value) => {
	  setEditedDescription(value ?? '');
  };

  const handleNewEditorChange = (value) => {
	  setNewEntry(prev => ({
		...prev,
		description: value ?? ''
	  }));
  };

  const handleSaveClick = () => {
    // Create updated entry - should have same fields as what backend expects
    const updatedEntry = { 
      id: selectedItem.name, // ID will be set to match name on backend
      name: selectedItem.name,
      description: editedDescription 
    };

    fetch(`${DDWN_API_BASE}/${selectedItem.category}/${selectedItem.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedEntry)
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to update entry');
        }
        return response.json();
      })
      .then(updatedItem => {
        // Add category to returned item
        const updatedItemWithCategory = { ...updatedItem, category: selectedItem.category };
        
        // Update data state
        setData(prevData =>
          prevData.map(item =>
            item.id === selectedItem.id && item.category === selectedItem.category
              ? updatedItemWithCategory
              : item
          )
        );
        
        // Update selected item
        setSelectedItem(updatedItemWithCategory);
        setIsEditing(false);
        setEditedDescription('');
      })
      .catch(error => console.error('Error updating entry:', error));
  };
  
    // Delete entry handler
  const handleDeleteClick = () => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      fetch(`${DDWN_API_BASE}/${selectedItem.category}/${selectedItem.id}`, {
        method: 'DELETE'
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to delete entry');
          }
          // Remove deleted item from the state
          setData(prevData =>
            prevData.filter(item => !(item.id === selectedItem.id && item.category === selectedItem.category))
          );
          setSelectedItem(null);
        })
        .catch(error => console.error('Error deleting entry:', error));
    }
  };

  // New entry handlers
  const handleAddNewClick = () => {
    setIsAddingNew(true);
    setSelectedItem(null);
    setIsEditing(false);
    setNewEntry({
      name: '',
      description: '',
      category: categories[0].value
    });
  };

  const handleNewEntryChange = (e) => {
    const { name, value } = e.target;
    setNewEntry(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNewSaveClick = () => {
    // Validate input
    if (!newEntry.name.trim()) {
      alert("Name is required!");
      return;
    }

    // Create entry to save - should include id that matches name
    const entryToSave = { 
      id: newEntry.name, // ID will match name
      name: newEntry.name,
      description: newEntry.description 
    };

    fetch(`${DDWN_API_BASE}/${newEntry.category}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entryToSave)
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to create new entry');
        }
        return response.json();
      })
      .then(createdEntry => {
        // Add category to returned item
        const entryWithCategory = { ...createdEntry, category: newEntry.category };
        
        // Update data state
        setData(prevData => [...prevData, entryWithCategory]);
        
        // Update selected item
        setSelectedItem(entryWithCategory);
        setIsAddingNew(false);
        setNewEntry({ name: '', description: '', category: categories[0].value });
      })
      .catch(error => console.error('Error creating entry:', error));
  };

  const handleNewCancelClick = () => {
    setIsAddingNew(false);
    setNewEntry({ name: '', description: '', category: categories[0].value });
  };
  
  // Helper function to get category label
  const getCategoryLabel = (categoryValue) => {
    return categories.find(c => c.value === categoryValue)?.label || categoryValue;
  };
  // Toggle collapse/expand of a category in the sidebar list
  const toggleCategoryCollapse = (categoryValue) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryValue]: !prev[categoryValue]
    }));
  };
  
  // Track the current category for grouping display
  let currentCategory = null;
  
  return (
    <div className="app-container" {...swipeHandlers}>
      {/* Add sidebar toggle button outside the sidebar */}
      <div className={`sidebar-toggle ${sidebarCollapsed ? 'collapsed' : ''}`} onClick={toggleSidebar}>
        {sidebarCollapsed ? '👉' : '👈'}
      </div>
      
      <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
		<div className="sidebar-header">
		  <input
			type="text"
			placeholder="Search by name or category..."
			value={searchTerm}
			onChange={(e) => setSearchTerm(e.target.value)}
			className="search-input"
		  />
		  <div className="master-search-toggle">
			<label>
			  <input
				type="checkbox"
				checked={masterSearchEnabled}
				onChange={() => setMasterSearchEnabled(!masterSearchEnabled)}
			  />
			  Search Full Text
			</label>

			<label style={{ marginLeft: '10px' }}>
			  <input
				type="checkbox"
				checked={showDDSpells}
				onChange={() => setShowDDSpells(!showDDSpells)}
			  />
			  Show DD Spells
			</label>
		  </div>
		  <div className="actions-row">
			<button onClick={handleAddNewClick}>
				Add New Entry
			</button>
			<button onClick={() => navigate('/megagame')} style={{ marginTop: '6px' }}>
				MegaGame
			</button>
      <button onClick={() => navigate('/cypher')} style={{ marginTop: '6px' }}>
        Cypher Atlas
      </button>
			<label className="dark-mode-toggle">
			  <input
				type="checkbox"
				checked={darkMode}
				onChange={() => setDarkMode(!darkMode)}
			  />
			  Dark Mode
			</label>
		  </div>
		</div>
        <ul className="results-list">
          {sortedResults.map((item, index) => {
            // Check if we need to add a category header
            const showCategoryHeader = item.category !== currentCategory;
            // Update current category
            currentCategory = item.category;
            const isCollapsed = !!collapsedCategories[item.category];
            
            return (
              <React.Fragment key={`${item.category}-${item.id}-${index}`}>
                {showCategoryHeader && (
                  <li
                    className={`category-header ${isCollapsed ? 'collapsed' : ''}`}
                    onClick={() => toggleCategoryCollapse(item.category)}
                  >
                    <span style={{ marginRight: '6px' }}>{isCollapsed ? '▸' : '▾'}</span>
                    {getCategoryLabel(item.category)}
                  </li>
                )}
                {!isCollapsed && (
                <li
                  className={
                    selectedItem &&
                    selectedItem.id === item.id &&
                    selectedItem.category === item.category
                      ? 'selected'
                      : ''
                  }
					onClick={() => {
                    setSelectedItem(item);
                    setIsEditing(false);
                    setIsAddingNew(false);
                    // NEW: Update URL with the selected item's name
                    window.history.pushState({}, '', `?item=${encodeURIComponent(item.name)}`);
                    // 2% chance to show easter egg content on click
                    if (Math.random() < 0.02) {
                      if (easterEggText != null) {
                        setEasterOverride(easterEggText);
                      } else {
                        fetch(easterEggUrl)
                          .then(r => r.text())
                          .then(text => {
                            setEasterEggText(text);
                            setEasterOverride(text);
                          })
                          .catch(() => setEasterOverride(null));
                      }
                    } else {
                      setEasterOverride(null);
                    }
                  }}
                >
                  {item.name}
                </li>
                )}
              </React.Fragment>
            );
          })}
          {sortedResults.length === 0 && (
            <li className="no-results">No matching items found</li>
          )}
        </ul>
      </div>
      <div className={`content-panel ${sidebarCollapsed ? 'expanded' : ''}`}>
        {isAddingNew ? (
          <div>
            <h2>Add New Entry</h2>
            <div>
              <label>
                Category:&nbsp;
                <select
                  name="category"
                  value={newEntry.category}
                  onChange={handleNewEntryChange}
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div>
              <label>
                Name:&nbsp;
                <input
                  type="text"
                  name="name"
                  value={newEntry.name}
                  onChange={handleNewEntryChange}
                />
              </label>
            </div>
            <div>
              <label>Description:</label>
				<MDEditor
				  value={newEntry.description}
				  onChange={handleNewEditorChange}
				  height={300}
				  preview="edit"
				/>
				<div
				  style={{ marginTop: '10px' }}
				  dangerouslySetInnerHTML={{ __html: mdParser.render(newEntry.description) }}
				/>
            </div>
            <div style={{ marginTop: '10px' }}>
              <button onClick={handleNewSaveClick}>Save</button>
              <button onClick={handleNewCancelClick}>Cancel</button>
            </div>
          </div>
        ) : selectedItem ? (
          <div>
            <h2>{selectedItem.name}</h2>
            <p className="category-label">{getCategoryLabel(selectedItem.category)}</p>
            {isEditing ? (
              <>
				<MDEditor
				  value={editedDescription}
				  onChange={handleEditorChange}
				  height={300}
				  preview="edit"
				/>
				<div
				  style={{ marginTop: '10px' }}
				  dangerouslySetInnerHTML={{ __html: mdParser.render(editedDescription) }}
				/>
                <div style={{ marginTop: '10px' }}>
                  <button onClick={handleSaveClick}>Save</button>
                  <button onClick={handleCancelClick}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div dangerouslySetInnerHTML={{ __html: mdParser.render(easterOverride ?? selectedItem.description) }} />
                <button onClick={handleEditClick}>Edit</button>
                <button onClick={handleDeleteClick} style={{ marginLeft: '10px', color: 'red' }}>
                  Delete
                </button>
              </>
            )}
          </div>
        ) : (
          <div>Please select an item to view its details or add a new entry.</div>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<HomeApp />} />
      <Route path="/megagame" element={<MegaGame />} />
      <Route path="/cypher" element={<CypherApp />} />
    </Routes>
  );
}


export default App;
